import '../tables.css';

import { useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../../recoil/atoms/userConfigState';
import { tenantSettingsState } from '../../../recoil/atoms/tenantSettingsState';
import { useSetSelectedPlayer } from '../../../recoil/hooks/useSetSelectedPlayer';
import { playerOverviewsState } from '../../../recoil/atoms/playerOverviewsState';
import { teamsState } from '../../../recoil/atoms/teamsState';
import { playerTeamDataSelector } from '../../../recoil/selectors/playerTeamDataSelector';
import { useOpenGlobalModal } from '../../../recoil/hooks/useOpenGlobalModal';
import { useAuthContext } from '../../../../common/contexts/useAuthContext';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import CloseIcon from '@mui/icons-material/Close';

import { getOwnTeamTableColumns, ownTeamTableColumnsWidth } from './ownTeamColumns';
import { translate } from '../../../../common/language/translations';
import { PlayerEntry, PlayerOverview, PlayerId } from '../../../types/player';
import { useWindowSize } from '../../../../common/hooks/WindowSize';
import { Dialog } from '@mui/material';
import { EditPlayerInfoModal } from '../../modals/EditPlayerInfoModal';
import { modalPaperProps } from '../../../static/constants/modals';
import { PlayerInfoCell } from '../tableCells';
import { getNavigationWidth } from '../../../utils/navigationUtils';
import { removePlayerFromAcademy } from '../../../services/firestore/players';
import { removePlayerFromAllAcademyTeams } from '../../../services/firestore/teams';
import { StringToAnyMap } from '../../../types/common';


interface OwnTeamTableProps {
  data: StringToAnyMap[];
  isAcademyTable?: boolean;
  tableSortBy: StringToAnyMap | undefined;
  setTableSortBy: (value: StringToAnyMap | undefined) => void;
  isAddPlayerView?: boolean;
  setIsAddPlayerView?: (value: boolean) => void;
}

export const OwnTeamTable: React.FC<OwnTeamTableProps> = ({
  data,
  isAcademyTable,
  tableSortBy,
  setTableSortBy,
  isAddPlayerView,
  setIsAddPlayerView,
}) => {

  const width = useWindowSize().width;
  const { currentUser } = useAuthContext();

  const userConfig = useRecoilValue(userConfigState);
  const tenantSettings = useRecoilValue(tenantSettingsState);
  const playerOverviews = useRecoilValue(playerOverviewsState);
  const teams = useRecoilValue(teamsState);
  const playerTeamData = useRecoilValue(playerTeamDataSelector);
  const { setAndTrackSelectedPlayer } = useSetSelectedPlayer();
  const { openPlayerViewModal, openConfirmModal } = useOpenGlobalModal();

  const [isEditPlayerInfoModalOpen, setIsEditPlayerInfoModalOpen] = useState(false);
  const [editPlayerInfoProperty, setEditPlayerInfoProperty] = useState<string | undefined>(undefined);
  const [playerToEdit, setPlayerToEdit] = useState<PlayerEntry | undefined>(undefined);


  const handleCellClick = (row: StringToAnyMap, property: string, event: React.MouseEvent<HTMLElement>) => {

    if (property === 'name') {
      setAndTrackSelectedPlayer({
        id: row.id,
        name: row.name,
        playerOverview: !isNaN(Number(row.id)) ? playerOverviews[Number(row.id)] : undefined,
      });
      return;
    }

    event.stopPropagation();

    if (property === 'team' || property === 'position') {
      openPlayerViewModal(
        row.id,
        row.name,
        undefined,
        true,
        true,
      );
    }

    else {
      setPlayerToEdit(row as PlayerEntry);
      setEditPlayerInfoProperty(property);
      setIsEditPlayerInfoModalOpen(true);
    }
  };


  const handleHeaderCellClick = (column: StringToAnyMap) => {
    if (tableSortBy && tableSortBy.id === column.property) {
      if (tableSortBy.desc) {
        setTableSortBy({ id: column.property, desc: false });
      }
      else {
        setTableSortBy(undefined);
      }
    }
    else {
      setTableSortBy({ id: column.property, desc: true });
    }
  };


  const confirmRemovePlayerFromAcademy = async (playerId: PlayerId) => {
    if (!userConfig?.club || !teams) return;

    const playerIsInOwnTeam = playerTeamData?.[playerId]?.['ownTeam'] ?? false;
    removePlayerFromAcademy(playerId, playerIsInOwnTeam, userConfig?.club, currentUser,);
    removePlayerFromAllAcademyTeams(playerId, teams, userConfig?.email, userConfig?.club, currentUser);
  };


  const handleRemovePlayerFromAcademy = (playerId: PlayerId) => {
    const playerIsInAnyAcademyTeam = teams
      && playerTeamData
      && playerTeamData[playerId]
      && Object.keys(playerTeamData[playerId]).some(teamId => teams[teamId]?.isAcademyTeam);

    if (playerIsInAnyAcademyTeam) {
      openConfirmModal(
        () => confirmRemovePlayerFromAcademy(playerId),
        'removePlayerFromAcademy',
        'removePlayerFromAcademyInfo',
      );
    }
    else {
      confirmRemovePlayerFromAcademy(playerId);
    }
  };


  const ownTeamColumns = useMemo(
    () => {
      if (!userConfig) return [];

      return getOwnTeamTableColumns(
        isAcademyTable ?? false,
        handleRemovePlayerFromAcademy,
        tenantSettings?.countryCode ?? 'NO',
        userConfig,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      tenantSettings?.countryCode,
      isAcademyTable,
      userConfig?.club,
      userConfig?.language,
      userConfig?.accessConfig.hasAccessToViewTeamRoles,
      userConfig?.accessConfig.hasAccessToEconomy,
    ]
  );

  const tableWidth = width - 24 - getNavigationWidth(userConfig) - (isAddPlayerView ? 350 : 0);
  const ownTeamTableStickyColumnWidth = userConfig?.accessConfig.hasAccessToEconomy ? 215 : 240;
  const ownTeamTableNonStickyWidth = tableWidth - ownTeamTableStickyColumnWidth;
  const ownTeamTableNonStickyMinWidth = ownTeamTableColumnsWidth(userConfig, isAcademyTable);
  const widthUnit = Math.max(ownTeamTableNonStickyWidth / ownTeamTableNonStickyMinWidth, 1);


  return (
    <div
      className='table-container player-table-container'
      style={{ overflowX: widthUnit <= 1 ? 'auto' : 'hidden' }}
    >

      <Dialog
        open={isEditPlayerInfoModalOpen}
        onClose={() => setIsEditPlayerInfoModalOpen(false)}
        slotProps={{ paper: modalPaperProps }}
      >
        <EditPlayerInfoModal
          editPlayerInfoProperty={editPlayerInfoProperty}
          playerToEdit={playerToEdit}
          closeModal={() => setIsEditPlayerInfoModalOpen(false)}
        />
      </Dialog>

      {/* Header */}
      <div className='table-header own-team-table-header player-table-header-background'>

        <div
          className='table-sticky-left own-team-table-header-section player-table-header-background'
          style={{ width: ownTeamTableStickyColumnWidth }}
        >
          {translate('players', userConfig?.language)}
        </div>

        {ownTeamColumns.map(column => {

          const paddingRight = column.paddingRight ?? 0;

          return (
            <div
              key={column.key}
              className='table-cell'
              style={{
                transition: isAcademyTable ? 'width 250ms' : undefined,
                width: column.width * widthUnit,
                cursor: column.isNotSortable ? undefined : 'pointer'
              }}
              onClick={() => column.isNotSortable ? null : handleHeaderCellClick(column)}
            >
              <div
                className='flex-row align-center'
                style={{ paddingRight: paddingRight, position: 'relative' }}
                title={column.title}
              >
                {column.isProtectedColumn && <AdminPanelSettingsIcon style={{ fontSize: 20, color: '#ffffffee', marginLeft: -4 }} />}
                {translate(column.key, userConfig?.language)}

                {tableSortBy && tableSortBy.id === column.property && (
                  <div style={{ position: 'absolute', right: paddingRight - 22 }}>
                    {tableSortBy.desc
                      ? <KeyboardArrowDownIcon style={{ fontSize: 22, marginBottom: -2 }} />
                      : <KeyboardArrowUpIcon style={{ fontSize: 22, marginBottom: -2 }} />
                    }
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>


      {/* Body */}
      <div>
        {data.map((row, rowIndex) => {

          const isSummaryRow = row.name === 'tableSummary';

          return (
            <div key={rowIndex} className={'table-row player-table-row' + (isSummaryRow ? ' own-team-table-summary-row' : '')}>

              <div
                className={
                  'table-sticky-left player-table-sticky-cell player-table-body-cell-with-border'
                  + (rowIndex % 2 === 0 ? ' own-team-table-cell-even' : ' own-team-table-cell-odd')
                  + (isSummaryRow ? '-no-hover' : '')
                }
                style={{ width: ownTeamTableStickyColumnWidth }}
                onClick={(event) => handleCellClick(row, 'name', event)}
              >
                {isSummaryRow && setIsAddPlayerView && (
                  <div className='own-team-table-add-player-icon-container'>
                    <div
                      className='own-team-table-add-player-icon'
                      title={translate('addPlayer', userConfig?.language)}
                      onClick={(e) => { e.stopPropagation(); setIsAddPlayerView(!isAddPlayerView); }}
                    >
                      {isAddPlayerView ? <CloseIcon style={{ fontSize: 22 }} /> : <PersonAddAlt1Icon style={{ fontSize: 22, marginRight: -3 }} />}
                    </div>
                  </div>
                )}

                {isSummaryRow && (
                  <div className='table-cell flex-column' style={{ paddingLeft: isAcademyTable ? 12 : 0 }}>
                    <div className='own-team-table-summary-cell-label'>
                      {translate('summary', userConfig?.language)}
                    </div>
                    {row.numberOfPlayers + ' ' + translate('players', userConfig?.language, true)}
                  </div>
                )}

                {!isSummaryRow && (
                  <PlayerInfoCell
                    playerOverview={row as PlayerOverview}
                    language={userConfig?.language}
                    showCountryFlag={false}
                    showClubInfo={false}
                    showTeamIcon={false}
                  />
                )}
              </div>

              {ownTeamColumns.map(column => {

                const hasWriteAccess =
                isAcademyTable // can always edit academy table
                  || userConfig?.accessConfig.hasAccessToModifyOwnTeam // need this access to edit own team table
                  || (isNaN(Number(row.id)) && (column.key === 'age' || column.key === 'country')) // can always edit age and country of players without data
                  || column.isProtectedColumn; // can always edit protected columns if you have access to them

                return (
                  <div
                    key={column.key}
                    className={
                      'table-cell'
                      + (rowIndex % 2 === 0 ? ' own-team-table-cell-even' : ' own-team-table-cell-odd')
                      + (
                        column.isButtonCell
                          ? '-no-hover'
                          : (isSummaryRow || !hasWriteAccess) ? ' no-hover' : ''
                      )
                    }
                    title={!isSummaryRow ? translate('edit', userConfig?.language) : ''}
                    style={{ transition: isAcademyTable ? 'width 250ms' : undefined, width: column.width * widthUnit }}
                    onClick={(event) => column.isButtonCell ? null : handleCellClick(row, column.property, event)}
                  >
                    {column.cell(row)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

    </div>
  );
};
